import { fixturePageFor } from "../../config/fixturePages";
import { request, requestVoid } from "./client";
import {
  billingPortalResponseSchema,
  boardResponseSchema,
  catalogueResponseSchema,
  chatHistoryResponseSchema,
  checkoutSessionResponseSchema,
  checkoutStatusSchema,
  upgradeAcceptedSchema,
  upgradePreviewSchema,
  conversationDetailSchema,
  disclaimerAckResponseSchema,
  entitlementsSchema,
  fantasyTeamSchema,
  fantasyTeamsResponseSchema,
  playerFormResponseSchema,
  seasonBoardResponseSchema,
  fixtureBoardResponseSchema,
  fixturePageSchema,
  fixturePagesBoardResponseSchema,
  fixturesPublicResponseSchema,
  playerIndexSchema,
  freeFixturesResponseSchema,
  meResponseSchema,
  savedLeagueResponseSchema,
  savedLeagueSchema,
  type ModelId,
  type SaveLeagueInput,
  type SavedLeagueSport,
  type Sport,
  type TierId,
} from "./schemas";

/**
 * Typed calls against the contract. One implementation each — these are the
 * contract + parsing, NOT a swap point. Mocking happens at the network boundary.
 */

export const api = {
  /** Public. Build status must be able to change without a frontend redeploy. */
  catalogue: () => request("/catalogue", catalogueResponseSchema),

  /**
   * TIMED OUT, unlike everything else here, and deliberately.
   *
   * Two guards block on this call, and both are written to FAIL OPEN when it
   * errors — RequireOnboarded says so at length, because sending someone to
   * /welcome when /welcome's own submit needs the same API is a redirect they
   * cannot satisfy. That intent only holds if the call actually finishes. A
   * request that never settles is not an error, so the fail-open never fires
   * and the whole app sits on a permanent LOADING… instead.
   *
   * Eight seconds is far beyond a healthy round trip for what is a small
   * metadata read, and an AbortError lands in exactly the branch the guards
   * already handle. NOT applied globally in client.ts: POST /chat can honestly
   * take half a minute, and a blanket timeout would cut a model mid-answer.
   */
  me: () => request("/me", meResponseSchema, { signal: AbortSignal.timeout(8_000) }),

  /** Polled by the checkout return page until the webhook lands. */
  entitlements: () => request("/me/entitlements", entitlementsSchema),

  /**
   * "I understand" on the pricing overlay in front of a chat thread.
   *
   * The version we send is the one the running bundle RENDERED, not the one
   * the server thinks is current — the server stores our claim unvalidated,
   * because what an audit needs is what the reader was actually shown.
   */
  acknowledgeDisclaimer: (disclaimerVersion: string) =>
    request("/me/disclaimer", disclaimerAckResponseSchema, {
      method: "POST",
      body: { disclaimerVersion },
    }),

  /**
   * Public, unauthenticated. In production this is a STATIC object (S3/CDN,
   * 5-min cache) regenerated daily by the pipeline — not a Lambda. The relative
   * path means the edge config decides where it really lives.
   *
   * ⚠️ THE CODE IS PART OF THE REQUEST, AND WAS NOT UNTIL 2026-08-30.
   * BoardPanel keyed its cache by sport and then called this for every one of
   * them, so every code shared one URL. Nothing broke only because `enabled` was
   * false for all but the NBA — enable a second and the page renders NBA
   * percentages under its heading, which is precisely the failure
   * Board.sports.test.tsx is written to prevent.
   *
   * Omitting the parameter still means the NBA server-side, so a bundle from
   * before this change keeps working mid-deploy. The reverse is NOT safe:
   * `?sport=football` against an API that predates the parameter is ignored and
   * answers with the NBA board — so the API ships before a second code is
   * enabled in SPORTS_WITH_A_BOARD.
   */
  board: (sport: Sport = "nba") =>
    request(`/teaser?sport=${encodeURIComponent(sport)}`, boardResponseSchema, {
      anonymous: true,
    }),

  /**
   * The Premium season board. Authenticated AND tier-gated server-side — a
   * free-tier caller gets 403 not_entitled, which the page renders as the
   * upsell panel rather than an error. The UI does not decide this; it reacts
   * to what the server said.
   */
  seasonBoard: () => request("/season/board", seasonBoardResponseSchema),

  /**
   * The Premium per-fixture board. Gated server-side exactly like the two above.
   *
   * ⚠️ ONE CALL SITE, ON PURPOSE. The intended path is `/football/fixtures`; it
   * cannot deploy yet, because deploying Kev-Platform-dev removes two
   * AdminHistoryFn exports that Kev-Console-dev imports and the console's branch
   * is unmerged. Lambda code ships without a stack deploy and routes do not, so
   * the artifact is selected by name on an existing gated route until then, and
   * moving it is a one-line change here.
   */
  fixtureBoard: () =>
    request("/season/board?artifact=football-fixtures", fixtureBoardResponseSchema),

  /**
   * ONE fixture's full page — Kev's read, the market comparison, movement,
   * props for both clubs, team news and the head-to-head guide. Gated exactly
   * like `fixtureBoard` above; `key` is the same fixture key that route's rows
   * carry (`league:date:Home:Away`), URL-encoded here so a club name with a
   * space or colon in it survives the trip.
   */
  /**
   * ⚠️ THE SPORT COMES FROM `config/fixturePages.ts`, NOT FROM A LITERAL HERE.
   * This call site was `/football/fixture/${…}` and there are now two routes;
   * a second hardcoded path is what left NFL rows unlinked in the first place.
   * `handler_season.py` reads the sport off the ROUTE's own template, so the
   * path is the whole request — get it wrong and a reader gets the other
   * sport's document, which is the one failure worse than a 404.
   */
  fixturePage: (sport: string, key: string) => {
    const source = fixturePageFor(sport);
    if (!source) {
      /* Never a guessed path. A sport with no entry has no page, and every
         caller is already `enabled`-gated on the same table — this is the
         belt for the braces. */
      return Promise.reject(new Error(`no fixture page for sport: ${sport}`));
    }
    return request(`${source.gatedPath}/${encodeURIComponent(key)}`, fixturePageSchema);
  },

  /**
   * The same pages as `fixturePage`, for the coming week at once, keyed by
   * fixture. Not called by the fixture page itself — that reads one key at a
   * time off the route param — kept here as the typed call for a future board
   * view over the same artifact, mirroring `fixtureBoard`'s `?artifact=`
   * idiom while `/football/fixture-pages` waits on the same stack deploy that
   * blocks `/football/fixtures`.
   */
  fixturePagesBoard: () =>
    request("/season/board?artifact=football-fixture-pages", fixturePagesBoardResponseSchema),

  /**
   * The free file — every upcoming fixture, Kev's own numbers only, no
   * market field anywhere. PUBLIC: `anonymous: true`, read by the signed-out
   * landing and the fixture page before entitlement is known, same posture
   * as `board` above.
   *
   * ⚠️ THE PATH IS `/teaser?artifact=fixtures-free`, NOT `/teaser/football?…`.
   * kev-docs/kev-football-fixture-page.md's "The free file" section names the
   * endpoint as `GET /teaser/football?artifact=fixtures-free`, but
   * `handler_teaser.py` (read 2026-09-07, mid-edit by another session) rides
   * the artifact selector on the SAME bare `/teaser` route the sport boards
   * use — `ARTIFACT_KEYS` is resolved independently of `?sport=`, exactly the
   * way `fixtureBoard` and `fixturePagesBoard` above ride `/season/board`'s
   * own selector rather than a route of their own. Built against the real
   * handler rather than the doc's spelling; flagging the mismatch for
   * whoever next edits the doc.
   */
  fixturesFree: (sport = "football") => {
    const source = fixturePageFor(sport);
    if (!source) return Promise.reject(new Error(`no free fixtures for sport: ${sport}`));
    return request(
      `/teaser?artifact=${source.freeArtifact}`,
      freeFixturesResponseSchema,
      { anonymous: true },
    );
  },

  /**
   * The ANONYMOUS file — every upcoming fixture across every sport, Kev's own
   * read with no numbers, and a value indicator that names the market never
   * the side. `GET /teaser?artifact=fixtures-public`, riding the same
   * dispatch point as `fixturesFree` above and for the same reason — see that
   * call's own note and `handler_teaser.py`'s "The fixtures-public artifact"
   * section.
   *
   * kev-docs/kev-landing-and-seo-plan.md (2026-09-07, the newest of the two
   * navigation docs and the one that governs): a three-tier product. This is
   * the tier a stranger and a search crawler see — `/app`'s landing list
   * fetches this, unconditionally and with no guard, and the fixture page one
   * level in is where an account (`fixturesFree`) and Premium
   * (`fixturePage`) add the rest.
   */
  fixturesPublic: () =>
    request("/teaser?artifact=fixtures-public", fixturesPublicResponseSchema, { anonymous: true }),

  /**
   * The NFL player index the fantasy squad form checks a typed name against.
   * `GET /teaser?artifact=players-nfl`, riding the same dispatch point as the two
   * calls above.
   *
   * ⚠️ ANONYMOUS ON PURPOSE, and it is the artifact that decides that rather than
   * the caller: the file carries names, positions and clubs from the licensed
   * feed and no projection, probability or price anywhere — nothing in it is a
   * thing we sell. `handler_teaser.py` gates it on LICENCE instead of on tier,
   * re-checking every served copy for ESPN-derived rows that may not be
   * republished.
   *
   * The squad form is reachable signed-out, so a gated index would mean the
   * warning "names are not checked" staying on screen for exactly the readers
   * most likely to be setting a squad up for the first time.
   */
  playerIndexNfl: () =>
    request("/teaser?artifact=players-nfl", playerIndexSchema, { anonymous: true }),

  /** The football player index: the NFL document's shape and normalisation, built from
   *  the squads Franco resolves names against. */
  playerIndexFootball: () =>
    request("/teaser?artifact=players-football", playerIndexSchema, { anonymous: true }),

  /**
   * The Premium player-form table. Same gate, same 403 → upsell behaviour.
   *
   * Fetched only when its tab is opened, not alongside the season board: it is
   * the larger of the two payloads and most visits never leave the first tab,
   * so eager-loading it would spend a gated S3 read per session on data nobody
   * looked at.
   */
  playerForm: () => request("/season/players", playerFormResponseSchema),

  /* `subscribeToMail` was here until 2026-08-28, posting to the public
     `POST /waitlist`. The landing page's mailing-list form is gone (Ned: "let's
     just keep it to a free account"), so the client no longer calls it.

     THE SERVER ENDPOINT STILL EXISTS. `handler_waitlist.py` is deployed and
     still answers; removing the caller does not retire the route, and the SES
     contact list still holds everyone who joined. What is gone is this repo's
     dependency on it — which is what unblocks RIPK #13, the double opt-in
     change that could not land while kev-web was calling the old shape. */

  /**
   * Server-created session. No Stripe key ever reaches the client — not even the
   * publishable one. The returned URL is OPAQUE: location.assign() it, never route.
   */
  checkoutSession: (input: {
    priceId: string;
    grants:
      | { kind: "model"; modelId: ModelId }
      | { kind: "tier"; tier: TierId }
      | { kind: "topup" };
    next: string;
  }) =>
    request("/billing/checkout-session", checkoutSessionResponseSchema, {
      method: "POST",
      body: input,
    }),

  /**
   * Move UP a plan, in place — no second subscription, no second charge.
   *
   * There is no URL to redirect to and nothing to `location.assign()`: the
   * customer already has a payment method on file, so the server swaps the price
   * on their existing subscription and Stripe credits the unused remainder of
   * the old plan against the new one. Answers 202, not 200, because entitlement
   * arrives on the webhook afterwards.
   */
  upgrade: (input: { priceId: string; grants: { kind: "tier"; tier: TierId } }) =>
    request("/billing/upgrade", upgradeAcceptedSchema, { method: "POST", body: input }),

  /**
   * The billing portal — cancel, change plan, or get an invoice.
   *
   * THE ONLY CANCEL PATH THERE IS. Until 2026-08-29 there was none: the API
   * served no portal route and this file had no call for one, while Account.tsx
   * told subscribers to "use the billing portal from your plan above" and the
   * Terms promised cancellation any time.
   *
   * NAMES NO CUSTOMER AND NO SUBSCRIPTION, and that is the security property
   * rather than an omission. The server resolves both from the caller's own
   * subscription rows, so an authenticated user cannot open a billing portal
   * over someone else's card — there is nowhere to put the id. The optional
   * `intent` chooses WHICH DOOR of the portal to open and nothing else; it
   * cannot widen what the caller may act on.
   *
   *   (none)   the portal's front page — cancel, cards, invoices
   *   update   Stripe's CONFIRM-THE-DIFFERENCE page for a named price. It states
   *            the proration and changes nothing until the customer confirms,
   *            which is the point: they are approving a charge on a card they
   *            gave to Stripe, so Stripe holds the record of what was agreed.
   *            Serves downgrades too — the pricing page had no control at all on
   *            the top plan before this.
   *   cancel   the same cancellation the front page offers, with the
   *            subscription named so a customer holding two cannot end the wrong
   *            one from a list.
   *
   * The returned URL is OPAQUE and cross-origin: `location.assign()` it, never
   * route to it. Same rule as `checkoutSession`, and useBillingPortal says why.
   */
  billingPortal: (input?:
    | { intent: "update"; priceId: string; grants: { kind: "tier"; tier: TierId } }
    | { intent: "cancel" }) =>
    request("/billing/portal", billingPortalResponseSchema,
      { method: "POST", ...(input ? { body: input } : {}) }),

  /** What that upgrade charges today. Always shown before charging it. */
  upgradePreview: (input: { priceId: string; grants: { kind: "tier"; tier: TierId } }) =>
    request("/billing/upgrade/preview", upgradePreviewSchema, {
      method: "POST",
      body: input,
    }),

  /**
   * Has fulfilment landed? Polled by the return page.
   *
   * Asking the SESSION rather than diffing entitlements is deliberate: a diff
   * has to know what "before" looked like, and the page leave through Stripe
   * destroys that. If the webhook wins the race, a diff sees no change and
   * concludes nothing happened — for a user who has just paid you.
   */
  checkoutStatus: (sessionId: string) =>
    request(`/billing/session/${encodeURIComponent(sessionId)}`, checkoutStatusSchema),

  /** The "recent conversations" list for one model. Scoped by modelId because
      each model's thread list is a separate surface in the UI — there's no
      cross-model history view. */
  chatHistory: (modelId: ModelId) =>
    request(`/chat/history?modelId=${encodeURIComponent(modelId)}`, chatHistoryResponseSchema),

  /** Full turn-by-turn detail for one thread, fetched only when it's opened —
      the history list above deliberately doesn't carry this much payload. */
  conversation: (id: string) =>
    request(`/chat/history/${encodeURIComponent(id)}`, conversationDetailSchema),

  /** 204 on success — no body, hence requestVoid rather than request. */
  deleteConversation: (id: string) =>
    requestVoid(`/chat/history/${encodeURIComponent(id)}`, { method: "DELETE" }),

  /**
   * Franco's saved squads.
   *
   * ⚠️ AUTHENTICATED BUT NOT SOLD, WHICH IS THE SERVER'S CHOICE AND WORTH KEEPING.
   * Reading, renaming and deleting your own squad costs nothing and needs no
   * subscription — only the conversation that BUILDS one does. So this call is the
   * one part of Franco's room a lapsed subscriber can still use, and the page is
   * written to let them: someone who stops paying keeps the list of what they
   * saved, and can still delete it. Do not add a tier check here to tidy the page.
   *
   * ⚠️ NBA ONLY, ENFORCED BY THE CALLER. The store has no sport column, so this
   * endpoint would happily hand an NBA roster to a page about football and
   * nothing in the payload would give it away. It is called from exactly one
   * place, behind `rules.has.savedTeams`.
   */
  fantasyTeams: () => request("/fantasy/teams", fantasyTeamsResponseSchema),

  /**
   * Rename a squad, or record the scoring format it is played under.
   *
   * ⚠️ THE ROSTER ITSELF IS NOT EDITABLE HERE, AND THE SERVER SAYS SO OUT LOUD:
   * a body carrying `players` is rejected with `players_not_editable_here`
   * rather than ignored. Only Franco's own tool writes a roster, because adding
   * or dropping a player runs a name resolver — typing a half-remembered name
   * into a text box and having it stored verbatim is how a squad ends up holding
   * a player who does not exist. The page therefore offers a name and a format,
   * and sends every roster change to the chat.
   */
  renameFantasyTeam: (id: string, input: { name?: string; format?: string }) =>
    request(`/fantasy/teams/${encodeURIComponent(id)}`, fantasyTeamSchema, {
      method: "PATCH",
      body: input,
    }),

  /** 204 whether or not it existed — the server will not confirm what it holds. */
  deleteFantasyTeam: (id: string) =>
    requestVoid(`/fantasy/teams/${encodeURIComponent(id)}`, { method: "DELETE" }),

  /**
   * The squad and league the reader typed into /app/fantasy, kept on the account.
   *
   * ⚠️ A DIFFERENT STORE FROM `fantasyTeams` ABOVE, AND THE DIFFERENCE IS THE
   * WHOLE REASON IT EXISTS. Every name in a saved TEAM came back from the player
   * dataset, because Franco's own tool resolved it before it was written; that
   * invariant is what stops an invented player getting a permanent home, and
   * `/fantasy/teams` refuses a body carrying `players` rather than weaken it.
   * A saved LEAGUE is free text somebody typed into a form with no player index
   * behind it. Persisting it to an account is a claim about WHERE it is kept and
   * about nothing else, and the panel says exactly that on screen.
   *
   * ⚠️ AUTHENTICATED, NOT SOLD — the same posture as the squads route above and
   * the same server decision. A lapsed subscriber keeps their roster and keeps
   * the route that erases it. Do not add a tier check.
   *
   * ⚠️ `revision: 0` IS A 200, NOT A 404: nothing saved yet is the ordinary
   * first state, and it must not arrive down the same path as a failed read.
   */
  savedLeague: (sport: SavedLeagueSport) =>
    request(`/fantasy/league/${encodeURIComponent(sport)}`, savedLeagueResponseSchema),

  /**
   * Replace this account's saved league for one code, WHOLE, against a revision.
   *
   * ⚠️ `ifRevision` IS NOT OPTIONAL AND MUST NEVER BE DEFAULTED. It is the token
   * that stops two devices silently overwriting each other, which is the only
   * failure this feature can cause that nothing else here can: losing a roster
   * mid-draft. The server refuses a body without it. A write against a stale
   * revision comes back 409 `saved_league_conflict` and writes NOTHING — the
   * caller then re-reads and puts both copies in front of the reader, because a
   * conflict resolved by guessing is a conflict resolved by discarding somebody's
   * squad. See `features/fantasy/useAccountLeague.ts`.
   *
   * ⚠️ WHOLE-OBJECT, NOT A PATCH. The squad and the shape are one thing a person
   * edits together; a partial update would let eleven names be stored against a
   * formation that was never sent, and the pitch would then be drawn from two
   * states that never coexisted.
   */
  saveLeague: (sport: SavedLeagueSport, input: SaveLeagueInput) =>
    request(`/fantasy/league/${encodeURIComponent(sport)}`, savedLeagueSchema, {
      method: "PUT",
      body: input,
    }),

  /**
   * Take it off the account. 204 whether or not anything was there.
   *
   * ⚠️ NO `ifRevision`, DELIBERATELY, AND THE SERVER AGREES. Deleting is the one
   * operation where a stale view costs nothing to honour: the intent is "I do not
   * want this kept", and a 409 over a revision the reader cannot see would leave
   * them unable to remove their own data because another device touched it. The
   * browser copy is untouched by this, and the panel says so before it is pressed.
   */
  deleteSavedLeague: (sport: SavedLeagueSport) =>
    requestVoid(`/fantasy/league/${encodeURIComponent(sport)}`, { method: "DELETE" }),
};
